/**
 * API dell'import (SPEC 002).
 *
 * ⚠️ Tutti i campi monetari sono STRINGHE decimali (ADR-0006): si formattano
 * con `lib/format.ts` e non si convertono mai in `number` per fare calcoli.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { dashboardKeys } from '@/features/dashboard/api';
import { portfolioKeys } from '@/features/portfolios/api';

export type CrossValidationStatus =
  | 'match'
  | 'mismatch'
  | 'only_pdf'
  /** Nello snapshot non c'è più: venduta. */
  | 'only_csv_closed'
  /** Lo snapshot non è stato caricato: il confronto non è possibile. */
  | 'only_csv';

export type ReportPosition = {
  isinOrTicker: string;
  name: string;
  assetType: string;
  section: string | null;
  current: {
    quantity: string;
    pricePerUnit: string | null;
    /** `null` se il PDF non ne dichiarava il valore in modo interpretabile. */
    valueEur: string | null;
    priceDate: string | null;
    /** La posizione è nota ma va valorizzata a parte (quotazioni). */
    valueMissing: boolean;
  } | null;
  history: {
    netQuantity: string;
    costBasisEur: string;
    realizedPlEur: string;
    feesPaidEur: string;
    taxesPaidEur: string;
    dividendsReceivedEur: string;
    transactionsCount: number;
    firstEventDate: string;
    lastEventDate: string;
    isClosed: boolean;
    costMethod: 'average';
  } | null;
  crossValidation: {
    status: CrossValidationStatus;
    pdfQuantity: string | null;
    csvNetQuantity: string | null;
    delta: string | null;
    note: string | null;
  };
  unrealizedPlEur: string | null;
  unrealizedPlPct: string | null;
};

export type PatrimonyReport = {
  generatedAt: string;
  inputsUsed: { pdf: boolean; csv: boolean };
  broker: string;
  accountRef: string | null;
  snapshot: {
    date: string;
    totalValueEur: string | null;
    sections: Record<string, string>;
    cashBalanceEur: string | null;
  } | null;
  history: {
    window: { from: string; to: string } | null;
    duplicatesRemoved: number;
    eventsCount: number;
  } | null;
  positions: ReportPosition[];
  cashFlows: Array<{ date: string; type: string; amountEur: string; description: string | null }>;
  totals: {
    currentValueEur: string | null;
    costBasisEur: string | null;
    unrealizedPlEur: string | null;
    realizedPlEur: string | null;
    depositsEur: string | null;
    withdrawalsEur: string | null;
    dividendsEur: string | null;
    interestEur: string | null;
    feesPaidEur: string | null;
    taxesPaidEur: string | null;
  };
  validation: {
    pdfSectionsSumMatchesTotal: boolean | null;
    csvDeduplicationApplied: boolean;
    positionsWithMismatch: string[];
    positionsOnlyInPdf: string[];
    positionsOnlyInCsvClosed: string[];
    /** Importate con quantità ma senza valore: i totali sono una sottostima. */
    positionsWithoutValue: string[];
    needsReview: boolean;
    warnings: string[];
  };
};

export type ImportResult = {
  importIds: string[];
  report: PatrimonyReport;
  applied: boolean;
  created: {
    portfolios: number;
    instruments: number;
    snapshots: number;
    holdings: number;
    transactions: number;
    skippedTransactions: number;
  };
};

export type ImportSummary = {
  id: string;
  originalFilename: string;
  fileType: string;
  sourceKind: string | null;
  detectedFormat: string | null;
  status: string;
  isDryRun: boolean;
  rowCount: number;
  importedRows: number;
  skippedRows: number;
  referenceDate: string | null;
  createdAt: string;
};

export const importKeys = {
  list: ['imports', 'list'] as const,
};

export type ImportPayload = {
  pdf?: File | undefined;
  csv?: File | undefined;
  dryRun: boolean;
};

function toFormData(payload: ImportPayload): FormData {
  const formData = new FormData();
  if (payload.pdf) formData.append('pdf', payload.pdf);
  if (payload.csv) formData.append('csv', payload.csv);
  return formData;
}

export function useImports() {
  return useQuery({
    queryKey: importKeys.list,
    queryFn: ({ signal }) => api.get<{ items: ImportSummary[]; total: number }>('/imports', signal),
  });
}

export function useRunImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ImportPayload) =>
      api.upload<ImportResult>(
        `/imports${payload.dryRun ? '?dryRun=true' : ''}`,
        toFormData(payload),
      ),
    onSuccess: (result) => {
      // Un'anteprima non cambia nulla: invalidare la cache la farebbe
      // ricaricare per niente. Un import applicato invece cambia tutto.
      if (!result.applied) return;
      void queryClient.invalidateQueries({ queryKey: importKeys.list });
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.summary });
      void queryClient.invalidateQueries({ queryKey: portfolioKeys.list });
    },
  });
}
